import type { ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { appConfig } from './app-config/app-config';

/** Title shown when no mounted component is claiming one. */
const GLOBAL_DOCUMENT_TITLE = 'Bloomfilter';

/** One active `useDocumentTitle` claim, held for as long as its component is mounted. */
interface DocumentTitleClaim {
  title: string;
  priority: number;
}

/**
 * Claims registered by every mounted `useDocumentTitle`, in registration order.
 * Module-level rather than component state: the winning title is a property of
 * the whole tree, so it cannot be resolved from inside any single component.
 */
const documentTitleClaims: DocumentTitleClaim[] = [];

/**
 * Applies the winning claim to `document.title`, falling back to the global
 * title once every claim has been released.
 *
 * The winner is the highest `priority`, and among equal priorities the most
 * recently registered. React flushes effects child-first, so equal-priority
 * claims resolve to the OUTERMOST component — preserving the long-standing
 * behaviour where a page container overrides a shared child (for example
 * `financials-tasks` over the `tasks-table` it renders).
 *
 * @returns {void}
 */
const applyDocumentTitle = (): void => {
  let next = GLOBAL_DOCUMENT_TITLE;

  if (documentTitleClaims.length > 0) {
    let winner = documentTitleClaims[0];
    for (const claim of documentTitleClaims) {
      if (claim.priority >= winner.priority) {
        winner = claim;
      }
    }
    next = winner.title;
  }

  // Claims are added and released on every mount/unmount, but most of those
  // churns do not change the winner. Skipping the no-op assignment keeps the
  // title stable for anything observing it (analytics, tests, the tab itself).
  if (document.title !== next) {
    document.title = next;
  }
};

/**
 * Sets the document title while the calling component is mounted, restoring
 * whichever title is still claimed by a mounted ancestor once it unmounts —
 * or the global title when nothing else claims one.
 *
 * Pass a `priority` above the default when a route must outrank the layout it
 * renders inside. A layout route sets its own title as a fallback at the
 * default priority, so every child URL is covered; a child that wants a more
 * specific title registers at a higher priority and wins for as long as it is
 * mounted, without the layout's title being lost on the way back out.
 *
 * @param {string} title - The document title to claim while mounted.
 * @param {number} priority - Higher wins over a lower-priority claim from an ancestor. Defaults to 0.
 * @returns {void}
 */
const useDocumentTitle = (title: string, priority = 0): void => {
  const claimRef = useRef<DocumentTitleClaim | null>(null);

  // Registration is bound to mount/unmount, NOT to the title. Re-pushing the
  // claim whenever the title changed would move it BEHIND an unchanged
  // ancestor's claim, silently flipping the equal-priority tie-break above and
  // letting a renamed child outrank the container that should win.
  // biome-ignore lint/correctness/useExhaustiveDependencies(title): claimed once on mount; the effect below syncs later renames in place
  // biome-ignore lint/correctness/useExhaustiveDependencies(priority): as above
  useEffect(() => {
    const claim: DocumentTitleClaim = { title, priority };
    claimRef.current = claim;
    documentTitleClaims.push(claim);
    applyDocumentTitle();

    return () => {
      const index = documentTitleClaims.indexOf(claim);
      if (index !== -1) {
        documentTitleClaims.splice(index, 1);
      }
      claimRef.current = null;
      applyDocumentTitle();
    };
  }, []);

  // Mutating the existing claim keeps its position in the array, so a component
  // that renames itself never changes who wins a tie.
  useEffect(() => {
    const claim = claimRef.current;
    if (!claim) {
      return;
    }
    claim.title = title;
    claim.priority = priority;
    applyDocumentTitle();
  }, [title, priority]);
};

/**
 * Method to get the date of the last day of the previous month.
 *
 * @returns the date of the last day of the previous month.
 */
const getRecentlyCompletedMonth = (): Date => {
  const currentDate = new Date();
  const firstDayCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  return new Date(firstDayCurrentMonth.getTime() - 24 * 60 * 60 * 1000);
};

/**
 * Removes duplicate items from an array based on their id property.
 *
 * @template T Type of array items, must contain an id property of type string
 * @param {T[]} items - Array of items to deduplicate
 * @returns {T[]} New array with duplicate items removed
 */
const deduplicateItems = <T extends { id: string }>(items: T[]): T[] => {
  const uniqueIds = new Set<string>();

  return items.filter((item) => {
    if (!uniqueIds.has(item.id)) {
      uniqueIds.add(item.id);
      return true;
    }
    return false;
  });
};

/**
 * Deduplicates items across multiple ComboboxItemGroup objects, keeping only the first occurrence of each unique item.
 *
 * @param {ComboboxItemGroup[]} groups - Array of ComboboxItemGroup objects to deduplicate
 * @returns {ComboboxItemGroup[]} New array of ComboboxItemGroup objects with duplicates removed and empty groups filtered out
 */
const deduplicateComboboxGroups = (groups: ComboboxItemGroup<ComboboxItem>[]): ComboboxItemGroup<ComboboxItem>[] => {
  const uniqueItemIds = new Set<string>();
  const seenItemIds = new Set<string>();

  // First pass - collect unique item IDs
  groups.forEach((group) => {
    group.items.forEach((item) => {
      if (typeof item !== 'string') {
        uniqueItemIds.add(item.value);
      }
    });
  });

  // Rebuild groups keeping only first occurrence of each unique item
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (typeof item === 'string') return false;

        if (uniqueItemIds.has(item.value) && !seenItemIds.has(item.value)) {
          seenItemIds.add(item.value);
          return true;
        }

        return false;
      }),
    }))
    .filter((group) => group.items.length > 0);
};

/**
 * Validates that a URL is safe for redirection or opening in a new window.
 * Prevents open redirect attacks by only allowing https: and http: protocols.
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is safe, false otherwise
 */
const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Allowlist of trusted OAuth provider domains for redirect validation.
 * Only redirect URLs whose hostname matches one of these domains (or a subdomain) are allowed.
 */
const ALLOWED_OAUTH_DOMAINS: readonly string[] = [
  'github.com',
  'atlassian.com',
  'atlassian.net',
  'microsoftonline.com',
  'visualstudio.com',
  'azure.com',
  'figma.com',
  'linear.app',
  'gitlab.com',
  'bitbucket.org',
  'asana.com',
  'aha.io',
  'notion.so',
];

/**
 * Validates that a URL is a safe OAuth redirect target.
 * Enforces HTTPS, disallows embedded credentials, and checks the hostname
 * against a known allowlist of OAuth provider domains to prevent open redirect attacks.
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is a safe OAuth redirect target, false otherwise
 */
const isAllowedOAuthRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    if (parsed.username || parsed.password) return false;
    if (!parsed.hostname) return false;

    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_OAUTH_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
};

/**
 * Resolves an `oauth_next` redirect target to the absolute URL to navigate to,
 * or `null` when it does not point back to the Bloomfilter backend origin.
 *
 * During the MCP OAuth flow the backend bounces an unauthenticated authorize
 * request through the FE login, passing its own `/o/login/` callback as the
 * `oauth_next` query param. A legitimate target therefore always shares an
 * origin with `VITE_APP_API_URL`. Returning the RESOLVED absolute URL (not a
 * boolean) is deliberate: a relative `oauth_next` is resolved against the
 * backend origin here, so the caller navigates the exact URL that was
 * validated. `window.location.assign(rawRelative)` would instead resolve the
 * relative against the FE origin and silently send the user to the wrong host.
 *
 * Any other origin, a non-http(s) scheme, or embedded credentials returns
 * `null`, so a crafted `oauth_next` cannot open-redirect the user off-site.
 * (`isSafeUrl` is insufficient here: it permits any http/https origin, so it
 * would allow `https://evil.com`.)
 *
 * @param {string} url - The oauth_next URL to validate
 * @returns {string | null} The absolute backend-origin URL to navigate to, or null if invalid
 */
const resolveBackendOAuthRedirectUrl = (url: string): string | null => {
  // Read via appConfig (runtime /config.js override layered over build-time
  // env), never import.meta.env directly — otherwise a rehosted build rejects
  // its real backend origin. See app-config.ts.
  const apiBaseUrl = appConfig.VITE_APP_API_URL;
  if (!apiBaseUrl) return null;
  try {
    const backendOrigin = new URL(apiBaseUrl).origin;
    const parsed = new URL(url, backendOrigin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    // Reject embedded credentials, mirroring isAllowedOAuthRedirectUrl —
    // .origin ignores userinfo, and userinfo in a redirect is a phishing trick.
    if (parsed.username || parsed.password) return null;
    if (parsed.origin !== backendOrigin) return null;
    return parsed.href;
  } catch {
    return null;
  }
};

/**
 * Validates that an `oauth_next` redirect target points back to the Bloomfilter
 * backend (the OAuth provider origin), preventing open-redirect attacks.
 *
 * @param {string} url - The oauth_next URL to validate
 * @returns {boolean} True if the URL targets the backend origin, false otherwise
 */
const isBackendOAuthRedirectUrl = (url: string): boolean => resolveBackendOAuthRedirectUrl(url) !== null;

/**
 * Resolves the validated backend redirect target from the current URL's
 * `oauth_next` param only, WITHOUT consuming the SSO stash — for render-phase
 * callers that must decide before triggering the redirect in an effect.
 *
 * @returns {string | null} The absolute backend URL to navigate to, or null if the param is absent or invalid
 */
const getValidatedOAuthNextTarget = (): string | null => {
  const fromUrl = new URLSearchParams(window.location.search).get('oauth_next');
  return fromUrl ? resolveBackendOAuthRedirectUrl(fromUrl) : null;
};

const OAUTH_NEXT_STASH_KEY = 'bf_oauth_next';

/**
 * Records (or clears) the `oauth_next` for the current login attempt so it can
 * survive an SSO IdP round-trip. The backend has NO channel to carry
 * `oauth_next` through SSO — the SAML/OIDC callbacks return the user to the FE
 * root with only `?refresh=&access=` — so the FE stashes a valid one before the
 * bounce and restores it on the token landing (sessionStorage, tab-scoped).
 *
 * A valid backend target is stored; an absent/off-origin target CLEARS any
 * existing stash. Callers pass the current attempt's `oauth_next`, so a fresh
 * login on a clean URL purges a stale value left by an abandoned earlier flow —
 * it must never be consumed by this unrelated login. A crafted value can never
 * be stored (validated against the backend origin first).
 *
 * @param {string | null} url - The current attempt's oauth_next; falsy/invalid clears the stash
 * @returns {void}
 */
const stashOAuthNext = (url: string | null): void => {
  try {
    if (url && resolveBackendOAuthRedirectUrl(url)) {
      window.sessionStorage.setItem(OAUTH_NEXT_STASH_KEY, url);
    } else {
      window.sessionStorage.removeItem(OAUTH_NEXT_STASH_KEY);
    }
  } catch {
    // sessionStorage unavailable (private mode / disabled) — best-effort only.
  }
};

/**
 * Reads and clears the stashed `oauth_next` (one-shot).
 *
 * @returns {string | null} The raw stashed oauth_next, or null if none is stashed
 */
const takeStashedOAuthNext = (): string | null => {
  try {
    const stashed = window.sessionStorage.getItem(OAUTH_NEXT_STASH_KEY);
    if (stashed !== null) {
      window.sessionStorage.removeItem(OAUTH_NEXT_STASH_KEY);
    }
    return stashed;
  } catch {
    return null;
  }
};

const OAUTH_REDIRECT_GUARD_KEY = 'bf_oauth_redirect_attempt';
// A backend OAuth forward the backend cannot satisfy (no session on the API
// host) bounces the user straight back to `/sign-in?oauth_next=…`, which would
// re-trigger the forward — an unbounded full-page loop. Stamp the time of each
// forward and treat a return within this window as a loop, stopping the retry.
const OAUTH_REDIRECT_LOOP_WINDOW_MS = 8_000;

/**
 * Reports whether a backend OAuth forward was attempted within the loop window —
 * i.e. the browser bounced straight back to the sign-in page, meaning the
 * backend could not establish a session and the forward must NOT be retried.
 *
 * @returns {boolean} True if a forward was stamped within the last OAUTH_REDIRECT_LOOP_WINDOW_MS
 */
const isBackendOAuthRedirectLooping = (): boolean => {
  try {
    const stampedAt = Number(window.sessionStorage.getItem(OAUTH_REDIRECT_GUARD_KEY));
    return stampedAt > 0 && Date.now() - stampedAt < OAUTH_REDIRECT_LOOP_WINDOW_MS;
  } catch {
    return false;
  }
};

/**
 * Stamps the current time as the latest backend OAuth forward attempt, arming the
 * one-shot loop guard read by isBackendOAuthRedirectLooping.
 *
 * @returns {void}
 */
const markBackendOAuthRedirectAttempt = (): void => {
  try {
    window.sessionStorage.setItem(OAUTH_REDIRECT_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / disabled) — guard degrades to a no-op.
  }
};

/**
 * Performs the post-login redirect back to the backend OAuth callback when an
 * `oauth_next` is present — in the current URL (password / already-authenticated
 * flows) or, failing that, in the SSO stash (restored after the IdP round-trip,
 * which strips the param). The stash is consumed only when the URL has no non-empty oauth_next.
 *
 * Centralizes the read + validate + navigate sequence shared by the password
 * login flow, the org-selection completion, and the SSO token landing, so the
 * open-redirect guard and the resolve-before-navigate contract cannot drift.
 * Every forward arms the loop guard, so a bounce-back from ANY of those callers
 * is detected — not only the one from SignIn.
 *
 * @returns {boolean} True if a valid oauth_next was found and navigation was triggered, false otherwise
 */
const redirectToBackendOAuthNext = (): boolean => {
  const fromUrl = new URLSearchParams(window.location.search).get('oauth_next');
  const candidate = fromUrl || takeStashedOAuthNext();
  const target = candidate ? resolveBackendOAuthRedirectUrl(candidate) : null;
  if (!target) {
    // A present-but-invalid URL param ends this continuation; drop any stale
    // stash so a later step in the same tab cannot consume it.
    if (fromUrl) {
      stashOAuthNext(null);
    }
    return false;
  }
  markBackendOAuthRedirectAttempt();
  window.location.assign(target);
  return true;
};

export {
  deduplicateComboboxGroups,
  deduplicateItems,
  getRecentlyCompletedMonth,
  getValidatedOAuthNextTarget,
  isAllowedOAuthRedirectUrl,
  isBackendOAuthRedirectLooping,
  isBackendOAuthRedirectUrl,
  isSafeUrl,
  markBackendOAuthRedirectAttempt,
  redirectToBackendOAuthNext,
  resolveBackendOAuthRedirectUrl,
  stashOAuthNext,
  takeStashedOAuthNext,
  useDocumentTitle,
};
